import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  getLayers as getLayersAction,
  updateArcgisToken,
} from "../actions/map";
import { LayerType } from "../../types/layer-type";
import {
  ArcgisHybridLayer,
  ArcgisTerrainLayer,
  Layer,
  ResponseLayer,
} from "../../interfaces/layer";
import Geometry from "@arcgis/core/geometry/Geometry";
import { CardBase } from "../../interfaces/card";
import {
  InfrastructureTypeIds,
  LayersDataTypes,
} from "../../types/layers-data-types";
import { UploadedFile } from "../../interfaces/utilities";
import { v4 as uuidv4 } from "uuid";
import { FieldInfo } from "../../interfaces/krookie";

export type MapSectionType =
  | "Search"
  | "Cities"
  | "Companies"
  | "Products"
  | "Favorites"
  | "Master Plan Modification Portal";

export interface ConvertedGeometry {
  [fileName: string]: {
    graphics: {
      geometry: string;
      attributes: Record<string, any>;
    }[];
    fields?: any[];
    layerName?: string;
    fileId: number;
  };
}

interface Screenshot {
  name: string;
  dataUrl: string;
}
interface Coordinate {
  latitude: number;
  longitude: number;
}

export type ScreenshotParamsPayload = ScreenshotParams | ScreenshotParams[];

export interface ScreenshotParams {
  name?: string;
  width?: number;
  height?: number;
  scale?: number;
  geometry: Geometry;
  layers?: Layer[];
  text?: string;
  style?: string;
  label_x?: number;
  label_y?: number;
  zoomAdjustment?: number;
  objectId?: number;
}

export interface ScreenshotResult {
  name: string;
  url: string;
}

export interface AsyncScreenshotActionPayload {
  data: ScreenshotParams[];
  deferred: {
    resolve: (screenshots: ScreenshotResult[]) => void;
    reject: (err: unknown) => void;
  };
}

export interface FocusOnFeatureParams extends CardBase {
  label: string;
  changeStyle?: boolean;
  addMarker?: boolean;
  zoomAdjustment?: number | null;
}

export interface ChangeDetailsObjectFromArcgisPayload {
  layer: Layer;
  objectId: number;
  newAttributes: Record<string, any>;
}

export interface ChangeDetailsObjectFromArcgisAsyncPayload
  extends ChangeDetailsObjectFromArcgisPayload {
  deferred: {
    resolve: (value: void | PromiseLike<void>) => void;
    reject: (reason?: any) => void;
  };
}

export enum MapSection {
  search = "search",
  masterPlan = "masterPlan",
  multiplePlot = "multiplePlot",
  masterStudy = "masterStudy",
  utilities = "utilities",
  none = "",
}
export type MapMode = "2d" | "3d";

interface MapState {
  mapMode: MapMode;
  section: MapSection;
  layers: Layer[];
  // layerDescriptions: {layers: Description[]},
  scale: number;
  rotationAngle: { prev: number; current: number };
  tokenLoading: boolean;
  arcgisTokens: Record<string, { token: string; expires: number } | null>;
  screenshot: Screenshot | null;
  inLoadingLayersId: number[];
  visibleLayersId: number[];
  coordinates: Coordinate;
  zoom: number;
  enableDrawingTools: boolean;
  drawingToolsIsActive: boolean;
  layersIds: Partial<Record<LayersDataTypes, number[]>>;
  mapComponentReady: MapSection;
  convertedGeometry: ConvertedGeometry | null;
  importModal: {
    selectedFiles: File[];
    selectedCoordinateSystem: number | null;
    error: string | null;
    isLoading: boolean;
    uploadedFiles: UploadedFile[];
  };
  attributesModal: {
    visible: boolean;
    selectedFeature: {
      layerName: string;
      attributes: Record<string, any>;
      fieldsInfo?: FieldInfo[] | null;
      layerUrl?: string | null;
    } | null;
  };
  userCanEditAttributes: boolean;
}

const initialState: MapState = {
  mapMode: "2d",
  section: MapSection.none,
  layers: [],
  // layerDescriptions: { layers: [] },
  scale: 0,
  rotationAngle: { prev: 0, current: 0 },
  tokenLoading: false,
  arcgisTokens: {},
  visibleLayersId: [],
  screenshot: null,
  inLoadingLayersId: [],
  coordinates: {
    latitude: 21.373159,
    longitude: 55.071379,
  },
  zoom: 6,
  enableDrawingTools: false,
  drawingToolsIsActive: false,
  layersIds: {},
  mapComponentReady: MapSection.none,
  convertedGeometry: null,
  importModal: {
    uploadedFiles: [],
    selectedFiles: [],
    selectedCoordinateSystem: null,
    error: null,
    isLoading: false,
  },
  attributesModal: {
    visible: false,
    selectedFeature: null,
  },
  userCanEditAttributes: false,
};

export const mapSlice = createSlice({
  name: "map",
  initialState,
  reducers: {
    changeOpacity: (state, { payload }) => {
      const layers = state.layers;
      const layer = layers.find((l) => l.id === payload.id);

      if (layer) layer.opacity = payload.opacity;

      state.layers = layers;
    },
    setLoading: (state, { payload }) => {
      if (payload.loading) {
        state.inLoadingLayersId = [...state.inLoadingLayersId, payload.id];
      } else if (!payload.loading) {
        state.inLoadingLayersId = state.inLoadingLayersId.filter(
          (item) => item !== payload.id,
        );
      }
    },
    setLayersVisible: (state, { payload }: PayloadAction<number[]>) => {
      state.visibleLayersId = payload.filter(
        (id, index) => payload.indexOf(id) === index,
      );
    },
    changeRotationAngle: (state, { payload }: PayloadAction<number>) => {
      state.rotationAngle = {
        prev: state.rotationAngle.current,
        current: payload,
      };
    },
    setScale: (state, { payload }: PayloadAction<number>) => {
      state.scale = Math.round(payload);
    },
    changeMapMode: (state) => {
      state.mapMode = state.mapMode === "2d" ? "3d" : "2d";
    },
    saveScreenshot: (state, { payload }) => {
      state.screenshot = payload;
    },
    clearScreenshots: (state) => {
      state.screenshot = null;
    },
    changeCoordinates: (state, { payload }: PayloadAction<Coordinate>) => {
      state.coordinates = payload;
    },
    setZoom: (
      state,
      { payload }: PayloadAction<{ scale?: number; zoom: number }>,
    ) => {
      if (payload.scale) {
        state.scale = Math.round(payload.scale);
      }
      state.zoom = payload.zoom;
    },
    setSection: (state, action: PayloadAction<MapSection>) => {
      state.section = action.payload;
    },
    setEnableDrawingTools: (state, action: PayloadAction<boolean>) => {
      state.enableDrawingTools = action.payload;
    },
    startDrawingTools: (state) => {
      state.drawingToolsIsActive = true;
    },
    stopDrawingTools: (state) => {
      state.drawingToolsIsActive = false;
    },
    setMapComponentReady: (state, action: PayloadAction<MapSection>) => {
      state.mapComponentReady = action.payload;
    },
    setConvertedGeometry: (
      state,
      action: PayloadAction<ConvertedGeometry | null>,
    ) => {
      const geometry = action.payload;

      if (!geometry) {
        state.convertedGeometry = null;
        return;
      }

      for (const file of Object.values(geometry)) {
        file.graphics.forEach((g) => {
          if (!g.attributes.uuid) {
            g.attributes.uuid = uuidv4();
          }
        });
      }

      if (state.convertedGeometry === null) {
        state.convertedGeometry = geometry;
      } else {
        state.convertedGeometry = {
          ...state.convertedGeometry,
          ...geometry,
        };
      }
    },
    setImportModalSelectedFiles: (state, action: PayloadAction<File[]>) => {
      state.importModal.selectedFiles = action.payload;
    },
    setImportModalSelectedCoordinateSystem: (
      state,
      action: PayloadAction<number | null>,
    ) => {
      state.importModal.selectedCoordinateSystem = action.payload;
    },
    setImportModalError: (state, action: PayloadAction<string | null>) => {
      state.importModal.error = action.payload;
    },
    setImportModalIsLoading: (state, action: PayloadAction<boolean>) => {
      state.importModal.isLoading = action.payload;
    },
    setImportModalUploadedFiles: (
      state,
      action: PayloadAction<UploadedFile[]>,
    ) => {
      state.importModal.uploadedFiles.push(...action.payload);
    },
    resetImportModalState: (state) => {
      state.importModal = initialState.importModal;
    },
    setAttributesModalSelectedFeature: (
      state,
      action: PayloadAction<{
        layerName: string;
        attributes: Record<string, any>;
        fieldsInfo?: FieldInfo[] | null;
        layerUrl?: string | null;
      } | null>,
    ) => {
      state.attributesModal.selectedFeature = action.payload;
      state.attributesModal.visible = !!action.payload;
    },
    updateAttributesModalFeatureAttributes: (
      state,
      action: PayloadAction<Record<string, any>>
    ) => {
      if (state.attributesModal.selectedFeature) {
        state.attributesModal.selectedFeature.attributes = {
          ...state.attributesModal.selectedFeature.attributes,
          ...action.payload,
        };
      }
    },
    resetAttributesModalState: (state) => {
      state.attributesModal = initialState.attributesModal;
    },
    setUserCanEditAttributes: (state, action: PayloadAction<boolean>) => {
      state.userCanEditAttributes = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      //   .addCase(getLayersDescription.fulfilled, (state, { payload }) => {
      //       state.layerDescriptions = payload;
      //   })
      //   .addCase(getLayersDescription.rejected, (state, { payload }) => {
      //       console.log("Error loading layers description " + payload)
      //       state.layerDescriptions = { layers: [] };
      //   })
      .addCase(getLayersAction.fulfilled, (state, { payload }) => {
        state.layers = [
          ArcgisHybridLayer,
          ArcgisTerrainLayer,
          ...payload.layers.map((l: ResponseLayer) => {
            let style3D = null;
            if (l.style3D && l.style3D.style) {
              style3D = JSON.parse(l.style3D.style);
            }
            let style2D = null;
            if (l.style2D && l.style2D.style) {
              style2D = JSON.parse(l.style2D.style);
            }
            let label2D = null;
            if (l.style2D && l.style2D.label) {
              label2D = JSON.parse(l.style2D.label);
            }
            if (style3D && style3D.onLoad)
              style3D.onLoad = eval(style3D.onLoad);
            return {
              id: l.layer.id,
              parentId: l.layer.parentLayerId,
              name: l.layer.name,
              url: l.layer.url,
              label: l.layer.label,
              type: l.layer.layerTypeId,
              dataType: l.layer.dataTypeId,
              visible: false,
              canEdit: l.canEdit,
              hidden: l.layer.hidden,
              style3D: {
                ...style3D,
                query: l.style3D ? l.style3D.query : null,
              },
              style2D: {
                ...style2D,
                query: l.style2D ? l.style2D.query : null,
                label: l.style2D ? label2D : null,
              },
            };
          }),
        ];
        const anyBaseMapLayer = state.layers.filter(
          (layer) => layer.dataType === LayersDataTypes.BaseMap,
        ).length;
        state.visibleLayersId = [
          ...state.visibleLayersId.filter(
            (item) => item != ArcgisHybridLayer.id,
          ),
        ];
        const visibleBaseMapLayer = state.layers.some(
          (layer) =>
            layer.dataType === LayersDataTypes.BaseMap &&
            state.visibleLayersId.includes(layer.id),
        );
        if (anyBaseMapLayer > 1 && !visibleBaseMapLayer)
          state.visibleLayersId.push(ArcgisHybridLayer.id);
        const defaultHost =
          typeof window !== "undefined" ? window.location.host : "default";
        state.arcgisTokens =
          payload.tokens ||
          (payload.token ? { [defaultHost]: payload.token } : {});
        const groups: Partial<Record<LayersDataTypes, number[]>> = {};
        for (const l of payload.layers) {
          const key = l.layer.dataTypeId as LayersDataTypes;
          const identifier = l.layer.parentLayerId
            ? l.layer.parentLayerId
            : l.layer.id;
          if (!groups[key]) groups[key] = [];
          if (!groups[key]!.includes(identifier)) groups[key]!.push(identifier);
        }
        state.layersIds = groups;
      })
      .addCase(getLayersAction.rejected, (state) => {
        state.layers = [];
      })
      .addCase(updateArcgisToken.pending, (state, { meta }) => {
        state.tokenLoading = true;
      })
      .addCase(updateArcgisToken.rejected, (state) => {
        state.tokenLoading = false;
        // keep existing tokens untouched on failure
      })
      .addCase(updateArcgisToken.fulfilled, (state, { payload }) => {
        state.tokenLoading = false;
        state.arcgisTokens = { ...state.arcgisTokens, ...(payload || {}) };
      });
  },
  selectors: {
    getLayers: createSelector(
      (state: MapState) => state,
      (state: MapState) =>
        state.layers.filter(
          (item: Layer) =>
            !item.parentId &&
            !item.hidden &&
            ((state.section === MapSection.search &&
              (state.mapMode === "3d"
                ? item.type !== LayerType.ArcgisMapServer
                : visibleArcgisLayerTypes.includes(item.type)) &&
              !hiddenMapDataTypes.includes(item.dataType)) ||
              (state.section === MapSection.multiplePlot &&
                visibleArcgisLayerTypes.includes(item.type) &&
                multiplePlotTypes.includes(item.dataType)) ||
              (state.section === MapSection.masterPlan &&
                visibleArcgisLayerTypes.includes(item.type) &&
                masterPlanDataTypes.includes(item.dataType)) ||
              (state.section === MapSection.masterStudy &&
                visibleArcgisLayerTypes.includes(item.type) &&
                masterCreateStudyTypes.includes(item.dataType)) ||
              (state.section === MapSection.utilities &&
                visibleArcgisLayerTypes.includes(item.type) &&
                masterUtilitiesTypes.includes(item.dataType))) &&
            (state.section === MapSection.masterStudy ||
              item !== ArcgisTerrainLayer),
        ),
    ),
  },
});
export const {
  changeOpacity,
  setLoading,
  setScale,
  setZoom,
  changeMapMode,
  setLayersVisible,
  changeRotationAngle,
  saveScreenshot,
  clearScreenshots,
  changeCoordinates,
  setSection,
  setEnableDrawingTools,
  startDrawingTools,
  stopDrawingTools,
  setMapComponentReady,
  setImportModalUploadedFiles,
  setConvertedGeometry,
  setImportModalIsLoading,
  setImportModalSelectedCoordinateSystem,
  setImportModalSelectedFiles,
  setImportModalError,
  resetImportModalState,
  setAttributesModalSelectedFeature,
  resetAttributesModalState,
  setUserCanEditAttributes,
  updateAttributesModalFeatureAttributes
} = mapSlice.actions;
export const { getLayers } = mapSlice.selectors;

export const validArcgisLayerTypes = [
  LayerType.ArcgisFeatureServer,
  LayerType.TMS,
  LayerType.WMS,
  LayerType.ArcgisMapServer,
  LayerType.Group,
  LayerType.GeoJSON,
];

const visibleArcgisLayerTypes = [...validArcgisLayerTypes];
export const masterPlanDataTypes = [
  LayersDataTypes.BaseMap,
  LayersDataTypes.Plots,
  LayersDataTypes.Map2D,
  LayersDataTypes.InvestmentOpportunities,
  LayersDataTypes.CityBoundary,
];

export const masterCreateStudyTypes = [
  LayersDataTypes.BaseMap,
  LayersDataTypes.CityBoundary,
  ...InfrastructureTypeIds,
  LayersDataTypes.Map2D,
  LayersDataTypes.OmanBorder,
  LayersDataTypes.Reports,
  LayersDataTypes.NCSI,
  LayersDataTypes.OPIS,
  LayersDataTypes.Plots,
  LayersDataTypes.InvestmentOpportunities,
];

export const masterUtilitiesTypes = [
  LayersDataTypes.BaseMap,
  LayersDataTypes.CityBoundary,
  LayersDataTypes.Map2D,
  LayersDataTypes.OmanBorder,
  LayersDataTypes.Plots,
  LayersDataTypes.InvestmentOpportunities,
  LayersDataTypes.ElectricityInfrastructure,
  LayersDataTypes.WaterInfrastructure,
  LayersDataTypes.TelecomInfrastructure,
  LayersDataTypes.SewerageInfrastructure,
  LayersDataTypes.GasInfrastructure,
];

export const hiddenMapDataTypes = [LayersDataTypes.NCSI, LayersDataTypes.OPIS];

export const multiplePlotTypes = [
  LayersDataTypes.BaseMap,
  LayersDataTypes.Plots,
];
